import { BellOutlined } from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { App, Badge, Button, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '../api/auth-provider.js';
import { notificationApi } from '../api/notification-api.js';
import { notificationDisplay } from '../api/notification-display.js';
import { notificationTargetPath } from '../api/notification-routing.js';
import { notificationStore } from '../api/notification-store.js';
import {
  type RealtimeNotification,
  connectRealtime,
  disconnectRealtime,
  getRealtimeSocket,
} from '../api/realtime.ts';
import { formatMoneyShort } from '../api/use-currency.js';
import i18n from '../i18n.js';
import { tx } from '../i18n/translate.js';

function shouldUseEnglish(identity?: Pick<AuthUser, 'isOverseas'> | null): boolean {
  return Boolean(identity?.isOverseas) || i18n.language?.startsWith('en');
}

/**
 * 实时通知铃铛 — header 右上角
 *
 * 自动连 WebSocket（登录后 token 已 ready），收到 'notification' 事件 push 到 inbox
 * 点击 bell 进入 Notifications 页面；实时 toast 点击跳转关联实体
 */
export function NotificationBell() {
  const { data: identity } = useGetIdentity<AuthUser & { token?: string }>();
  const { message, notification } = App.useApp();
  const navigate = useNavigate();
  const unread = useSyncExternalStore(notificationStore.subscribe, notificationStore.getUnread);
  const tokenRef = useRef<string | null>(null);

  const refreshUnreadNotifications = useCallback(() => {
    notificationApi
      .list(100)
      .then((nextItems) => notificationStore.hydrate(nextItems))
      .catch(() => {
        /* 通知历史加载失败不影响实时连接 */
      });
  }, []);

  const openNotification = useCallback(
    (n: RealtimeNotification) => {
      const target = notificationTargetPath(n);
      if (target) navigate(target);
      if (n.id) {
        notificationStore.markOneRead(n.id);
        notificationApi
          .markRead(n.id)
          .then((result) => {
            if (result.oaTodoPending || result.oaTodoFailed) {
              message.warning(result.message ?? tx('notificationMessages.oaClosePending'));
            }
          })
          .catch(() => undefined);
        return;
      }
      notificationStore.markOneRead();
    },
    [message, navigate],
  );

  const onNotification = useCallback(
    (n: RealtimeNotification) => {
      notificationStore.push(n);
      const display = notificationDisplay(n, shouldUseEnglish(identity));
      notification.open({
        message: display.title,
        description: display.body ?? (n.amount ? formatMoneyShort(n.amount) : undefined),
        placement: 'topRight',
        duration: 5,
        onClick: () => openNotification(n),
      });
    },
    [identity, notification, openNotification],
  );

  useEffect(() => {
    refreshUnreadNotifications();
    const timer = window.setInterval(refreshUnreadNotifications, 15_000);
    const onFocus = () => refreshUnreadNotifications();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshUnreadNotifications();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshUnreadNotifications]);

  // 接 token 来连 — identity 是触发器（登录后变化时重 connect）
  useEffect(() => {
    // identity from refine — 实际 token 存 localStorage 'crest_token' (与 auth-provider 一致)
    const stored = identity?.token ?? localStorage.getItem('crest_token');
    if (!stored) return;
    if (tokenRef.current === stored && getRealtimeSocket()?.connected) return;
    tokenRef.current = stored;
    const sock = connectRealtime(stored);
    sock.on('notification', onNotification);
    sock.on('connect', refreshUnreadNotifications);
    return () => {
      sock.off('notification', onNotification);
      sock.off('connect', refreshUnreadNotifications);
      // 不 disconnect — 切页面时保留连接
    };
  }, [identity, onNotification, refreshUnreadNotifications]);

  // 卸载时清理（一般 app 整体只挂一次）
  useEffect(() => {
    return () => {
      disconnectRealtime();
    };
  }, []);

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', height: '100%', lineHeight: 1 }}>
      <Badge
        className="crest-header-badge"
        count={unread}
        overflowCount={99}
        offset={[-2, 2]}
        style={{ backgroundColor: '#ff4d4f', boxShadow: '0 0 0 1px #fff' }}
      >
        <Tooltip title={tx('hardcoded:components.notificationbell.09896576')}>
          <Button
            type="text"
            className="crest-header-action"
            icon={<BellOutlined style={{ fontSize: 16 }} />}
            onClick={() => navigate('/app/crm/inbox?tab=notification')}
          />
        </Tooltip>
      </Badge>
    </div>
  );
}
